import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Save } from "lucide-react";

const DAY_LABELS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const SHORT_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function dayLabelFromDate(dateIso) {
  // getDay() returns 0=Sunday..6=Saturday
  const d = new Date(dateIso + "T12:00:00");
  return SHORT_LABELS[d.getDay()];
}
const COUNTER_FIELDS = [
  { key: "contactos",        label: "C",  full: "Contactos" },
  { key: "reunioes",         label: "R",  full: "Reuniões" },
  { key: "angariacoes",      label: "A",  full: "Angariações" },
  { key: "vendas",           label: "V",  full: "Vendas" },
  { key: "social_posts",     label: "P",  full: "Posts" },
  { key: "humanized_videos", label: "V*", full: "Vídeos" },
  { key: "formacoes",        label: "F",  full: "Formações" },
];

const EMPTY_DAY = COUNTER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: 0 }), {});

/**
 * DailyReportGrid — 7-day Mon-Sun grid for self-reporting daily activity.
 * Persists each day independently via POST /api/daily-entries.
 * The weekly_entries aggregate is auto-recomputed server-side.
 *
 * Props:
 *  - playerId: consultant's player.id
 *  - week: competition week number to render
 */
export default function DailyReportGrid({ playerId, week, disabled = false }) {
  const [data, setData] = useState({ entries: [], week_dates: [], today: "" });
  const [drafts, setDrafts] = useState({}); // { date: {field: value} }
  const [savingDate, setSavingDate] = useState(null);

  const refresh = useCallback(() => {
    if (!playerId || !week) return;
    api.get(`/daily-entries?week=${week}&player_id=${playerId}`)
      .then((r) => { setData(r.data); setDrafts({}); })
      .catch((e) => console.debug("daily fetch failed:", e?.message));
  }, [playerId, week]);

  useEffect(() => { refresh(); }, [refresh]);

  // Build a {date: entry} map merging server data + pending drafts
  const byDate = useMemo(() => {
    const m = {};
    for (const d of data.week_dates) m[d] = { ...EMPTY_DAY, ...drafts[d] };
    for (const e of data.entries) m[e.date] = { ...m[e.date], ...e, ...drafts[e.date] };
    return m;
  }, [data, drafts]);

  const weekTotals = useMemo(() => {
    const t = { ...EMPTY_DAY };
    for (const d of data.week_dates) {
      for (const f of COUNTER_FIELDS) t[f.key] += Number(byDate[d]?.[f.key] || 0);
    }
    return t;
  }, [byDate, data.week_dates]);

  const setField = (date, key, value) => {
    const v = Math.max(0, parseInt(value || "0", 10) || 0);
    setDrafts((d) => ({ ...d, [date]: { ...d[date], [key]: v } }));
  };

  const saveDay = async (date) => {
    setSavingDate(date);
    try {
      const counters = COUNTER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: Number(byDate[date]?.[f.key] || 0) }), {});
      await api.post("/daily-entries", { player_id: playerId, date, ...counters });
      toast.success(`${dayLabelFromDate(date)} guardado`);
      refresh();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingDate(null); }
  };

  if (!data.week_dates.length) {
    return <div className="kw-card p-4 text-[#737373] text-sm">A carregar dias da semana…</div>;
  }

  return (
    <div className="kw-card p-5" data-testid="daily-report-grid">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="kw-overline">Reporting diário</div>
          <h3 className="font-display text-xl font-black uppercase">
            Semana {week}
            {data.week_dates.length > 0 && (
              <span className="text-[#737373] font-normal ml-2 normal-case text-sm">
                · {formatShortDate(data.week_dates[0])} → {formatShortDate(data.week_dates[data.week_dates.length - 1])}
              </span>
            )}
          </h3>
        </div>
        <WeekTotals totals={weekTotals} />
      </div>

      <div className="grid gap-2">
        {data.week_dates.map((date) => (
          <DayRow
            key={date}
            date={date}
            dayLabel={dayLabelFromDate(date)}
            values={byDate[date] || EMPTY_DAY}
            isToday={date === data.today}
            isDirty={!!drafts[date]}
            saving={savingDate === date}
            disabled={disabled}
            onChange={(k, v) => setField(date, k, v)}
            onSave={() => saveDay(date)}
          />
        ))}
      </div>

      <p className="text-[10px] uppercase tracking-widest text-[#737373] mt-3">
        C: Contactos · R: Reuniões · A: Angariações · V: Vendas · P: Posts · V*: Vídeos · F: Formações · Os totais semanais são calculados automaticamente.
      </p>
    </div>
  );
}

function DayRow({ date, dayLabel, values, isToday, isDirty, saving, disabled = false, onChange, onSave }) {
  return (
    <div className={`border p-3 flex flex-wrap items-center gap-3 ${isToday ? "border-[#E50000]/40 bg-[#E50000]/5" : "border-[#222]"} ${disabled ? "opacity-60" : ""}`}
      data-testid={`day-row-${date}`}>
      <div className="min-w-[100px]">
        <div className="font-display font-black uppercase text-sm">{dayLabel}{isToday && <span className="ml-1 text-[10px] text-[#E50000]">· hoje</span>}</div>
        <div className="text-[10px] text-[#737373]">{formatShortDate(date)}</div>
      </div>
      {COUNTER_FIELDS.map((f) => (
        <div key={f.key} className="flex flex-col">
          <label className="text-[9px] uppercase tracking-widest text-[#737373] mb-0.5">{f.label}</label>
          <input
            type="number" min="0"
            className="kw-input !py-1 !text-sm !w-16 text-center"
            value={values[f.key] ?? 0}
            disabled={disabled}
            onChange={(e) => onChange(f.key, e.target.value)}
            data-testid={`day-${date}-${f.key}`}
          />
        </div>
      ))}
      <button
        onClick={onSave}
        disabled={!isDirty || saving || disabled}
        className="ml-auto kw-btn-primary !py-1.5 !px-3 text-[10px] uppercase tracking-widest font-bold disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
        data-testid={`day-save-${date}`}>
        <Save className="w-3 h-3" /> {saving ? "..." : disabled ? "Bloq." : "Guardar"}
      </button>
    </div>
  );
}

function WeekTotals({ totals }) {
  return (
    <div className="flex gap-3 text-xs flex-wrap">
      {COUNTER_FIELDS.map((f) => (
        <div key={f.key} className="text-center border border-[#222] px-2 py-1">
          <div className="text-[9px] uppercase tracking-widest text-[#737373]">{f.label}</div>
          <div className="font-display font-black">{totals[f.key]}</div>
        </div>
      ))}
    </div>
  );
}

function formatShortDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("pt-PT", { day: "2-digit", month: "short" }); }
  catch { return iso; }
}

function dayLabelFor(date, weekDates) {
  // Deprecated: kept for compatibility. Prefer dayLabelFromDate(date).
  return dayLabelFromDate(date);
}
